import type React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { config } from 'app/config';

const ACADEMY_BG_IMAGE = '/academy-bg.png';

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  container-type: inline-size;
  font-family: ${({ theme }) => theme.typography.poppins};
  background-color: ${({ theme }) => theme.colors.charcoal};
  background-image:
    radial-gradient(446.97% 89.58% at 50% 49.86%, #212223 0%, rgba(33, 34, 35, 0.00) 100%),
    url(${ACADEMY_BG_IMAGE});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  line-height: normal;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      aspect-ratio: 768 / 355;
      background-image:
        linear-gradient(270deg, #212223 56.12%, rgba(33, 34, 35, 0.00) 66.67%),
        url(${ACADEMY_BG_IMAGE});
    `,
    )}

  ${({ theme: { media } }) =>
    media.large(
      () => `
      aspect-ratio: 1400 / 387;
      background-image:
        linear-gradient(270deg, #212223 37.14%, rgba(33, 34, 35, 0.00) 71.22%),
        url(${ACADEMY_BG_IMAGE});
      background-position: 0.03rem -4.5rem;
      background-size: 100% 183%;
    `,
    )}
`;

const ContentArea = styled.div`
  display: flex;
  height: 100%;
  align-items: flex-end;
  ${({ theme: { spacing } }) => `padding: ${spacing(6)} ${spacing(4)};`}

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      align-items: center;
      justify-content: flex-end;
      padding: ${spacing(8)} ${spacing(4)};
    `,
    )}
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme: { spacing } }) => `gap: ${spacing(3)};`}

  ${({ theme: { media } }) => media.medium(() => 'width: 50%;')}
  ${({ theme: { media } }) => media.large(() => 'width: 42%;')}
`;

const AccentLabel = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.focoltone};

  ${({ theme: { media } }) => media.medium(() => 'font-size: clamp(0.75rem, 1.143cqw, 1rem);')}
`;

const Heading = styled.h2`
  margin: 0;
  font-weight: 600;
  font-size: 1.375rem;
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: -0.75px;

  ${({ theme: { media } }) => media.medium(() => 'font-size: clamp(1.125rem, 2cqw, 1.75rem);')}
`;

const Description = styled.p`
  margin: 0;
  font-weight: 400;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.white};

  ${({ theme: { media } }) => media.medium(() => 'font-size: clamp(0.75rem, 1.143cqw, 1rem);')}
`;

const CTALink = styled.a`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${({ theme }) => theme.colors.focoltone};
  color: ${({ theme }) => theme.colors.black};
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  font-weight: ${({ theme }) => theme.typography.bold};
  font-size: 0.75rem;
  white-space: nowrap;
  text-decoration: none;
  transition: background-color 0.15s ease-in-out;

  ${({ theme: { media } }) => media.medium(() => 'font-size: clamp(0.625rem, 0.857cqw, 0.75rem);')}
  ${({ theme: { media } }) => media.large(() => 'align-self: flex-start;')}

  &:hover {
    background-color: ${({ theme }) => theme.colors.caparol};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.pascol};
  }
`;

const AcademyBanner: React.FC = () => {
  const intl = useIntl();

  return (
    <Wrapper>
      <ContentArea>
        <TextGroup>
          <AccentLabel>{intl.formatMessage({ id: 'academy.label' })}</AccentLabel>
          <Heading>{intl.formatMessage({ id: 'academy.title' })}</Heading>
          <Description>{intl.formatMessage({ id: 'academy.description' })}</Description>
          <CTALink href={config.REACT_APP_ACADEMY_URL} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage({ id: 'academy.cta' })}
          </CTALink>
        </TextGroup>
      </ContentArea>
    </Wrapper>
  );
};

export default AcademyBanner;
